import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import { FormikSelect } from 'components/Formik';
import Title from 'components/Title';
import { useIntegrationSyncPolling } from 'hooks/common/useIntegrationSyncPolling';
import { useFetchBankAccounts } from 'hooks/integrations/useFetchBankAccounts';
import { useFetchExpenseAccounts } from 'hooks/integrations/useFetchExpenseAccounts';
import { useSaveBankExpenseAccount } from 'hooks/integrations/useSaveBankExpenseAccounts';
import { useSyncQuickbook } from 'hooks/integrations/useSyncQuickbookOnline';

import { initialBankExpenseData, bankExpenseDataValidationSchema } from './schema';

const ChartAccountMapping = () => {
  interface IPostValues {
    bank_account?: string;
    expense_account?: string;
  }

  const { t } = useTranslation(['common', 'employees', 'jobsTasks', 'integrations']);

  const { status: quickbookSyncStatus, mutateAsync: quickbookSyncMutateAsync, data: syncData } = useSyncQuickbook();

  const onQuickbookSync = () => {
    quickbookSyncMutateAsync();
    syncLongPollingStatus.current = 'fetching';
  };

  const { data: bankData, refetch: refetchBankData } = useFetchBankAccounts();
  const { data: expenseData, refetch: refetchExpenseData } = useFetchExpenseAccounts();
  const { mutate, isSuccess } = useSaveBankExpenseAccount();

  const { syncLongPollingStatus } = useIntegrationSyncPolling({ id: syncData?.payload?.id, refetch: refetchBankData });

  const handleFormSubmit = (values: any) => {
    const postData: IPostValues = {
      bank_account: values.bank_account?.id ?? '0',
      expense_account: values.expense_account?.id ?? '0',
    };
    mutate(postData);
    if (isSuccess) {
      refetchBankData();
      refetchExpenseData();
    }
  };

  return (
    <Box>
      <Title>{t('integrations:quickbooks.onlineMappingHeading')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={quickbookSyncStatus === 'loading' || syncLongPollingStatus.current === 'fetching'}
          onClick={onQuickbookSync}
        >
          {t('integrations:quickbooks.syncOnline')}
        </Button>
      </Flex>
      <Stack alignItems="center" ml={10} gridGap={3}>
        <Formik
          initialValues={initialBankExpenseData(bankData?.payload?.bankAccount, expenseData?.payload?.expenseAccount)}
          enableReinitialize={true}
          validationSchema={bankExpenseDataValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {() => (
            <Form style={{ width: '100%' }}>
              <Stack alignItems="center">
                <Flex mt={4} width="100%" gridGap={4}>
                  <FormikSelect
                    options={bankData?.payload?.accounts}
                    formControlProps={{ isRequired: true }}
                    name="bank_account"
                    label={t('integrations:quickbooks.searchBankAccounts')}
                    selectProps={{
                      isClearable: true,
                      isSearchable: true,
                      placeholder: t('integrations:quickbooks.searchBankAccounts'),
                      debounceTimeout: 300,
                      getOptionLabel: (option: any) => option.name,
                      getOptionValue: (option: any) => option.id,
                    }}
                  />
                  <FormikSelect
                    name="expense_account"
                    options={expenseData?.payload?.accounts}
                    formControlProps={{ isRequired: true }}
                    label={t('integrations:quickbooks.searchExpenseAccounts')}
                    selectProps={{
                      isClearable: true,
                      isSearchable: true,
                      placeholder: t('integrations:quickbooks.searchExpenseAccounts'),
                      debounceTimeout: 300,
                      getOptionLabel: (option: any) => option.name,
                      getOptionValue: (option: any) => option.id,
                    }}
                  />
                </Flex>
                <Button width="25%" size="md" type="submit">
                  {t('common:saveChanges')}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Stack>
    </Box>
  );
};

export default ChartAccountMapping;
